export type ShortcutPlatform = "mac" | "windows";

export type ShortcutIconName =
    | "enter"
    | "shift"
    | "arrow-up"
    | "arrow-right"
    | "arrow-down"
    | "arrow-left"
    | "backspace"
    | "space";

export type ShortcutKey =
    | {
          kind: "letter" | "symbol" | "equal" | "text";
          label: string;
          name: string;
      }
    | { kind: "icon"; icon: ShortcutIconName; name: string };

/** One chord: keys pressed together. */
export type ShortcutStep = ShortcutKey[];

const sharedKeys: ReadonlyArray<[string, ShortcutKey]> = [
    ["shift", { kind: "icon", icon: "shift", name: "Shift" }],
    ["enter", { kind: "icon", icon: "enter", name: "Enter" }],
    ["arrowup", { kind: "icon", icon: "arrow-up", name: "Arrow up" }],
    ["arrowright", { kind: "icon", icon: "arrow-right", name: "Arrow right" }],
    ["arrowdown", { kind: "icon", icon: "arrow-down", name: "Arrow down" }],
    ["arrowleft", { kind: "icon", icon: "arrow-left", name: "Arrow left" }],
    ["backspace", { kind: "icon", icon: "backspace", name: "Backspace" }],
    ["space", { kind: "icon", icon: "space", name: "Space" }],
    ["escape", { kind: "text", label: "Esc", name: "Escape" }],
    ["delete", { kind: "symbol", label: "⌫", name: "Delete" }],
    ["tab", { kind: "symbol", label: "⇥", name: "Tab" }],
    ["=", { kind: "equal", label: "=", name: "Equals" }],
];

const keyMaps: Record<ShortcutPlatform, ReadonlyMap<string, ShortcutKey>> = {
    mac: new Map([
        ...sharedKeys,
        ["mod", { kind: "symbol", label: "⌘", name: "Command" }],
        ["meta", { kind: "symbol", label: "⌘", name: "Command" }],
        ["alt", { kind: "symbol", label: "⌥", name: "Option" }],
        ["ctrl", { kind: "symbol", label: "⌃", name: "Control" }],
    ]),
    windows: new Map([
        ...sharedKeys,
        ["mod", { kind: "text", label: "Ctrl", name: "Control" }],
        ["meta", { kind: "text", label: "Win", name: "Windows" }],
        ["alt", { kind: "text", label: "Alt", name: "Alt" }],
        ["ctrl", { kind: "text", label: "Ctrl", name: "Control" }],
    ]),
};

const toShortcutKey = (
    rawKey: string,
    keyMap: ReadonlyMap<string, ShortcutKey>,
): ShortcutKey => {
    const mappedKey = keyMap.get(rawKey.toLowerCase());

    if (mappedKey !== undefined) {
        return mappedKey;
    }

    if (rawKey.length === 1) {
        const label = rawKey.toUpperCase();

        return { kind: "letter", label, name: label };
    }

    return { kind: "text", label: rawKey, name: rawKey };
};

/** One key's label as text, e.g. `mod` is ⌘ on Mac and Ctrl elsewhere. */
export const getShortcutKeyLabel = (
    rawKey: string,
    platform: ShortcutPlatform,
) => {
    const key = toShortcutKey(rawKey, keyMaps[platform]);
    return key.kind === "icon" ? key.name : key.label;
};

const isPresent = (value: string) => value.length > 0;

// A `+` that ends a step is the plus key itself, so `+` and `mod++` keep it.
const chordSeparator = /\+(?!$)/;

/**
 * Turns a hotkey string (`useKeyboardCommand` grammar) into the chips to draw:
 * `>` splits a sequence into steps, `+` splits a step into a chord.
 */
export const formatShortcut = (
    keys: string,
    platform: ShortcutPlatform,
): ShortcutStep[] => {
    const keyMap = keyMaps[platform];

    return keys
        .split(">")
        .map((step) => step.trim())
        .filter(isPresent)
        .map((step) =>
            step
                .split(chordSeparator)
                .map((rawKey) => rawKey.trim())
                .filter(isPresent)
                .map((rawKey) => toShortcutKey(rawKey, keyMap)),
        );
};

const hasMacUserAgentData = (browserNavigator: Navigator): boolean => {
    if (!("userAgentData" in browserNavigator)) {
        return false;
    }

    const { userAgentData } = browserNavigator;

    return (
        typeof userAgentData === "object" &&
        userAgentData !== null &&
        "platform" in userAgentData &&
        userAgentData.platform === "macOS"
    );
};

const detectPlatform = (): ShortcutPlatform => {
    if (typeof navigator === "undefined") {
        return "windows";
    }

    const isMac =
        /Mac|iPod|iPhone|iPad/.test(navigator.userAgent) ||
        hasMacUserAgentData(navigator);

    return isMac ? "mac" : "windows";
};

export const currentPlatform: ShortcutPlatform = detectPlatform();

/** Whether the platform's primary modifier is held: ⌘ on Apple platforms, Ctrl elsewhere. */
export const isPrimaryModifier = ({
    metaKey,
    ctrlKey,
}: Pick<KeyboardEvent, "metaKey" | "ctrlKey">) =>
    currentPlatform === "mac" ? metaKey : ctrlKey;
